import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Package, Monitor, Laptop, Keyboard, Mouse, Headphones, Car, Building2, CreditCard,
  CheckCircle, Clock, RefreshCw, Eye, AlertTriangle, Calendar, IndianRupee,
  Shield, FileText, History
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const ASSET_STATUS = {
  AVAILABLE: { label: 'Available', color: 'bg-green-100 text-green-700' },
  ASSIGNED: { label: 'Assigned', color: 'bg-blue-100 text-blue-700' },
  MAINTENANCE: { label: 'Under Maintenance', color: 'bg-amber-100 text-amber-700' },
  DAMAGED: { label: 'Damaged', color: 'bg-red-100 text-red-700' },
  RETIRED: { label: 'Retired', color: 'bg-gray-100 text-gray-700' },
  LOST: { label: 'Lost', color: 'bg-red-100 text-red-700' }
};

const MyAssets = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [myAssets, setMyAssets] = useState([]);
  const [assetHistory, setAssetHistory] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [acceptanceNotes, setAcceptanceNotes] = useState('');
  const [acceptLoading, setAcceptLoading] = useState(false);

  useEffect(() => {
    fetchMyAssets();
  }, []);

  const fetchMyAssets = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/assets/my-assets`);
      setMyAssets(response.data.current_assets || []);
      setAssetHistory(response.data.asset_history || []);
    } catch (error) {
      console.error('Error fetching my assets:', error);
      toast.error('Failed to fetch your assets');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAsset = async () => {
    if (!selectedAsset) return;
    
    setAcceptLoading(true);
    try {
      await api.post(`/assets/assignments/${selectedAsset.assignment_id}/accept`, {
        notes: acceptanceNotes
      });
      toast.success('Asset accepted successfully');
      setAcceptDialogOpen(false);
      setSelectedAsset(null);
      setAcceptanceNotes('');
      fetchMyAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept asset');
    } finally {
      setAcceptLoading(false);
    }
  };

  const getIconComponent = (iconName) => {
    const icons = { Monitor, Laptop, Keyboard, Mouse, Headphones, Car, Building2, CreditCard, Package };
    return icons[iconName] || Package;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const pendingAcceptance = myAssets.filter(a => !a.accepted);
  const acceptedAssets = myAssets.filter(a => a.accepted);

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="animate-spin text-indigo-600" size={32} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="my-assets-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My Assets</h1>
            <p className="text-slate-500">View and manage company assets assigned to you</p>
          </div>
          <Button onClick={fetchMyAssets} variant="outline" className="gap-2">
            <RefreshCw size={16} /> Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Package className="text-white" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{myAssets.length}</p>
                  <p className="text-xs text-blue-600">Total Assigned</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500 rounded-lg">
                  <Clock className="text-white" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{pendingAcceptance.length}</p>
                  <p className="text-xs text-amber-600">Pending Acceptance</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <CheckCircle className="text-white" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{acceptedAssets.length}</p>
                  <p className="text-xs text-green-600">Accepted Assets</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Acceptance Alert */}
        {pendingAcceptance.length > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-600 mt-0.5" size={20} />
                <div>
                  <h4 className="font-semibold text-amber-800">Action Required</h4>
                  <p className="text-sm text-amber-700">
                    You have {pendingAcceptance.length} asset(s) pending your acceptance. Please review and accept them to confirm receipt.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Assets Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package size={20} />
              Currently Assigned Assets ({myAssets.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {myAssets.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package size={48} className="mx-auto mb-4 opacity-50" />
                <p>No assets currently assigned to you</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Serial Number</TableHead>
                    <TableHead>Assigned Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {myAssets.map((asset) => {
                    const IconComp = getIconComponent(asset.icon);
                    return (
                      <TableRow key={asset.assignment_id} data-testid={`asset-row-${asset.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded-lg">
                              <IconComp className="text-slate-600" size={18} />
                            </div>
                            <div>
                              <p className="font-medium">{asset.name}</p>
                              <p className="text-sm text-slate-500">{asset.brand} {asset.model}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{asset.asset_type_name}</TableCell>
                        <TableCell className="font-mono text-sm">{asset.serial_number}</TableCell>
                        <TableCell>{formatDate(asset.assigned_date)}</TableCell>
                        <TableCell>
                          {asset.accepted ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle size={12} className="mr-1" />
                              Accepted
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">
                              <Clock size={12} className="mr-1" />
                              Pending Acceptance
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => { setSelectedAsset(asset); setViewDialogOpen(true); }}
                              title="View Details"
                            >
                              <Eye size={16} />
                            </Button>
                            {!asset.accepted && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => { setSelectedAsset(asset); setAcceptDialogOpen(true); }}
                                className="text-green-600 hover:text-green-700"
                                title="Accept Asset"
                                data-testid={`accept-btn-${asset.id}`}
                              >
                                <CheckCircle size={16} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Asset History */}
        {assetHistory.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History size={20} />
                Asset History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Assigned Date</TableHead>
                    <TableHead>Returned Date</TableHead>
                    <TableHead>Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assetHistory.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{item.asset_name}</TableCell>
                      <TableCell>{item.asset_type}</TableCell>
                      <TableCell>{formatDate(item.assigned_date)}</TableCell>
                      <TableCell>{formatDate(item.returned_date)}</TableCell>
                      <TableCell>{item.duration_days || '-'} days</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* View Asset Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Asset Details</DialogTitle>
            </DialogHeader>
            {selectedAsset && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-sm text-slate-500">Basic Information</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-slate-500">Name:</span> <span className="font-medium">{selectedAsset.name}</span></p>
                      <p><span className="text-slate-500">Type:</span> {selectedAsset.asset_type_name}</p>
                      <p><span className="text-slate-500">Serial:</span> <span className="font-mono">{selectedAsset.serial_number}</span></p>
                      <p><span className="text-slate-500">Brand:</span> {selectedAsset.brand || '-'}</p>
                      <p><span className="text-slate-500">Model:</span> {selectedAsset.model || '-'}</p>
                      <p><span className="text-slate-500">Condition:</span> {selectedAsset.condition}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-sm text-slate-500">Assignment Info</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-slate-500">Assigned:</span> {formatDate(selectedAsset.assigned_date)}</p>
                      <p><span className="text-slate-500">Expected Return:</span> {formatDate(selectedAsset.expected_return_date) || 'N/A'}</p>
                      <p><span className="text-slate-500">Location:</span> {selectedAsset.location || '-'}</p>
                      {selectedAsset.warranty_expiry && (
                        <p><span className="text-slate-500">Warranty:</span> {formatDate(selectedAsset.warranty_expiry)}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {selectedAsset.notes && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-sm text-slate-500">Notes</h4>
                    <p className="text-sm">{selectedAsset.notes}</p>
                  </div>
                )}
                
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <Shield size={16} />
                    Your Responsibility
                  </h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Keep this asset safe and in good condition</li>
                    <li>• Report any damage or issues immediately to IT/Admin</li>
                    <li>• Return the asset in working condition when required</li>
                    <li>• Do not share with unauthorized persons</li>
                  </ul>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Accept Asset Dialog */}
        <Dialog open={acceptDialogOpen} onOpenChange={setAcceptDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Accept Asset Handover</DialogTitle>
            </DialogHeader>
            {selectedAsset && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      {React.createElement(getIconComponent(selectedAsset.icon), { className: 'text-indigo-600', size: 24 })}
                    </div>
                    <div>
                      <p className="font-medium">{selectedAsset.name}</p>
                      <p className="text-sm text-slate-500">{selectedAsset.asset_type_name} • {selectedAsset.serial_number}</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <FileText size={16} />
                    Acceptance Acknowledgment
                  </h4>
                  <p className="text-sm text-amber-700 mb-3">
                    By accepting this asset, you acknowledge:
                  </p>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>• I have received the above asset in working condition</li>
                    <li>• I will be responsible for its safekeeping</li>
                    <li>• I will return it in the same condition upon request or exit</li>
                    <li>• Any damage/loss may result in recovery of asset value</li>
                  </ul>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Notes (Optional)</label>
                  <Textarea
                    value={acceptanceNotes}
                    onChange={(e) => setAcceptanceNotes(e.target.value)}
                    placeholder="Any remarks about the asset condition..."
                    rows={2}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setAcceptDialogOpen(false); setAcceptanceNotes(''); }}>Cancel</Button>
              <Button 
                onClick={handleAcceptAsset} 
                disabled={acceptLoading}
                className="bg-green-600 hover:bg-green-700 gap-2"
              >
                {acceptLoading ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                Accept Asset
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MyAssets;
